import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS,
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedSourceAppKey,
	type InvitedAppShortcutAllowedTargetAppKey,
} from '../../common/types';

export type GlobalShortcutsItemMap = {
	[key: string]: GlobalShortcutsItem;
};

export const ProductsInState = new Set([
	...INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS,
	...INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
]);
export type ProductInState =
	| InvitedAppShortcutAllowedTargetAppKey
	| InvitedAppShortcutAllowedSourceAppKey;

export const IneligibilityReason = {
	EXPERIMENT_OFF: 'EXPERIMENT_OFF',
	ERROR: 'ERROR',
	CACHE_EMPTY: 'CACHE_EMPTY',
	CACHE_EXPIRED: 'CACHE_EXPIRED',
	NO_TARGET_PRODUCTS: 'NO_TARGET_PRODUCTS',
	NO_ACCESS_TO_TARGET_PRODUCTS: 'NO_ACCESS_TO_TARGET_PRODUCTS',
	TARGET_PRODUCTS_ALL_ACTIVE: 'TARGET_PRODUCTS_ALL_ACTIVE',
	SOURCE_PRODUCT_TOO_NEW: 'SOURCE_PRODUCT_TOO_NEW',
} as const;
export type IneligibilityReason = (typeof IneligibilityReason)[keyof typeof IneligibilityReason];

export type ExperimentDetails = IneligibleExperimentDetails | EligibleExperimentDetails;

export type IneligibleExperimentDetails = {
	ineligibilityReason: IneligibilityReason;
	invitedApps?: undefined;
};

export type EligibleExperimentDetails = {
	ineligibilityReason: null;
	invitedApps: {
		[app in InvitedAppShortcutAllowedTargetAppKey]?: InvitedAppDetails;
	};
};

export type InvitedAppDetails = {
	collaborators: Array<{ avatar: string } | null>;
	eligible: true;
	firstImpressionAt: StoredTimestamp | null;
};

export const CACHE_WRITE_ID_KEY = 'updateId';

export const FirstActiveSpecialValues = {
	NOT_CHECKED: undefined,
	INACTIVE: null,
};
export type FirstActiveSpecialValues =
	(typeof FirstActiveSpecialValues)[keyof typeof FirstActiveSpecialValues];

export type CachedState = {
	apps?: {
		[app in ProductInState]?: CachedAppState;
	};

	// Internal field.
	// Used to check if we can write to the cache, or if it's blocked/full.
	// We'll try to read this field, increment it, write, and read it back.
	[CACHE_WRITE_ID_KEY]?: number;

	firstActiveUpdatedTimestamp?: StoredTimestamp;
};

export type CachedAppState = {
	access?: boolean; // From Switcher BFF. Does the user have access to this app?
	firstActive?: StoredTimestamp | FirstActiveSpecialValues; // From User Traits. null == checked but not active
	firstImpressionAt?: StoredTimestamp;
} & (
	| {
			collaboratorCount: number;
			collaboratorDetails: { avatar: string }[];
	  }
	| {
			collaboratorCount?: undefined;
			collaboratorDetails?: undefined;
	  }
);

export type FullContext = {
	cache: InvitedAppShortcutsCache;
	cloudId: string;
	enabled: boolean;
	sourceProduct: InvitedAppShortcutAllowedSourceAppKey;

	targetProducts: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;

	/**
	 * Allow null only because the Jira tenant context hook declares that it may return it.
	 * Actually passing null will prevent fetching traits.
	 */
	userId: string | null;
};

export type StoredTimestamp = number;

export type Trait = { name: string; value: string | boolean | number };
export type Traits = Trait[];
export interface InvitedAppShortcutsCache {
	read: ({ ssr }: { ssr: boolean }) => CachedState | null;
	write: (state: CachedState) => Promise<boolean>;
}
